/**
 *
 * Pagination
 *
 */
import React, { PropsWithChildren, useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  selectPaginationContentOnlyByType,
  selectPagination_NoContentByType,
} from './slice/selectors';
import { usePaginationSlice } from './slice';
import { Box, useTheme, TablePagination, IconButton } from '@mui/material';
import colors from 'app/assets/styles/colors';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import image from 'app/assets/Empty-Member.png';
import _ from 'lodash';
import { PaginationType } from './slice/types';
import { makeThemeStyle } from 'styles/makeThemeStyle';

interface Props {
  loadingEl?: React.ReactNode;
  emptyEl?: React.ReactNode;
  renderToolBar?: boolean;
  paginationType: PaginationType;
}

export interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
}

const useStyles = makeThemeStyle(theme => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

export function TablePaginationActions(props: TablePaginationActionsProps) {
  const { count, page, rowsPerPage, onPageChange } = props;
  const classes = useStyles();
  const handleFirstPageButtonClick = (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(e, 0);
  };

  const handleBackButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(e, page - 1);
  };

  const handleNextButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(e, page + 1);
  };

  const handleLastPageButtonClick = (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
        size="large"
      >
        <FirstPageIcon />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
        size="large"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1 || count === 0}
        aria-label="next page"
        size="large"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1 || count === 0}
        aria-label="last page"
        size="large"
      >
        <LastPageIcon />
      </IconButton>
    </div>
  );
}

export const Pagination: React.FC<PropsWithChildren<Props>> = props => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { paginationType, renderToolBar = true } = props;
  const {
    actions: { initPageData, goToPage, resetPagination, clearData },
  } = usePaginationSlice();

  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    cur_page,
    total_pages,
    total_count,
    page_size,
    query_info: { filters, sorts, page_size: queryPageSize, page, path },
  } = useSelector(
    selectPagination_NoContentByType(paginationType),
    shallowEqual,
  );
  const content = useSelector(
    selectPaginationContentOnlyByType(paginationType),
    _.isEqual,
  );

  useEffect(() => {
    if (path) {
      dispatch(clearData(paginationType));
      dispatch(
        initPageData({
          path,
          filters,
          sorts,
          page_size: queryPageSize,
          page,
          pagiType: paginationType,
        }),
      );
    }
  }, [
    path,
    filters,
    sorts,
    queryPageSize,
    dispatch,
    clearData,
    initPageData,
    page,
    paginationType,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetPagination(paginationType));
    };
  }, [dispatch, resetPagination, paginationType]);

  const handleOnChange = (e, value) => {
    if (!content[value]?.data || content[value].error) {
      dispatch(
        initPageData({
          path,
          filters,
          sorts,
          page_size: queryPageSize,
          page: value,
          pagiType: paginationType,
        }),
      );
    } else {
      dispatch(goToPage({ pagiType: paginationType, page: value }));
    }
  };

  if (
    Object.keys(content).length === 0 &&
    (total_pages === 0 || total_count === 0)
  ) {
    return null;
  }

  if (
    path &&
    !Object.keys(content)
      .filter(key => content[key].data?.length > 0)
      .includes(cur_page.toString()) &&
    !content[cur_page]?.loading
  ) {
    return (
      <Box display="flex" mt={'auto'} mb={'auto'} justifyContent="center">
        {props.emptyEl || (
          <Box display="flex" flexDirection="column">
            <Box>
              <img src={image} alt="Empty search result" />
            </Box>
            <Box
              textAlign="center"
              color={theme.palette.asseticPrimary.main}
              mt={2}
              fontWeight="bold"
            >
              Nothing to see here!
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  let rendering;
  if (content[cur_page]?.loading) {
    rendering = props.loadingEl || <div>Loading...</div>;
  } else if (content[cur_page]?.error) {
    rendering = <div>Error...</div>;
  } else {
    rendering = props.children;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      justifyContent="space-between"
    >
      <Box pb={4}>{rendering}</Box>
      <Box
        position="sticky"
        right={16}
        bottom={0}
        width={'100%'}
        bgcolor={colors.white}
      >
        {renderToolBar && (
          <TablePagination
            component={'div'}
            count={total_count}
            page={cur_page}
            onPageChange={handleOnChange}
            rowsPerPage={page_size}
            rowsPerPageOptions={[5]}
            ActionsComponent={TablePaginationActions}
          />
        )}
      </Box>
    </Box>
  );
};

export default Pagination;
